import cx from 'classnames';

// Types
import { PaymentDeductionsListProps } from './PaymentDeductionsList.types';

// Config
import { ANIMATION_TYPES } from '@constants/giftOptionsConstants';
import last3Digits from '@utils/string/lastDigitsHelper';

// Components
import PaymentDeductionsItem from './PaymentDeductionsItem';
import CreditRemainingAmount from '@components/account-credit-remaining-amount';
import Heading from '@components/heading';

// Styles
import styles from './payment-deductions-list.scss';

const PaymentDeductionsList = ({
  animation,
  applyGiftPaymentFailed = false,
  balance = '',
  infoMessage = '',
  hideBalance = false,
  isRemovable = false,
  list = [],
  onRemove = () => {},
  onUndo = () => {},
  orderTotalCovered = false,
  promoCodeLoadingCancelled = false,
  remainingBalance = '',
  remainingBalanceCardNumber = '',
  showRemaining = false,
  showUndo = false,
  title = '',
  type,
  unusedGiftCards,
}: PaymentDeductionsListProps) => {
  const listIsEmpty = list.length === 0;
  const unusedGiftCardsEmpty = unusedGiftCards?.length === 0;

  if (listIsEmpty && unusedGiftCardsEmpty) {
    return null;
  }

  const shouldShowBalance = !listIsEmpty && !hideBalance;

  return (
    <div
      className={cx(styles.giftOptionsAppliedInnerContainer, {
        [styles.slideDown]: animation === ANIMATION_TYPES.WHOLE_SECTION,
      })}
    >
      {list.length > 0 && (
        <>
          {title && <Heading label={title} marginBottom="1" tag="h4" type="xs" />}
          <div className={styles.list} data-testid="payment-deductions-list-used-gift-cards">
            {list.map((item, i) => (
              <div
                key={item.id}
                className={cx({
                  [styles.slideDown]: animation === ANIMATION_TYPES.NEW_ITEM && i === 0,
                  [styles.appliedFlash]: animation && i === 0,
                })}
              >
                <PaymentDeductionsItem
                  {...item}
                  alert={i === 0}
                  applyGiftPaymentFailed={applyGiftPaymentFailed}
                  balance={balance}
                  isRemovable={isRemovable}
                  onRemove={() => onRemove(item, type)}
                  onUndo={() => onUndo(item, type)}
                  orderTotalCovered={orderTotalCovered}
                  promoCodeLoadingCancelled={promoCodeLoadingCancelled}
                  showUndo={showUndo}
                  type={type}
                />
              </div>
            ))}
          </div>
        </>
      )}

      {showRemaining && (
        <CreditRemainingAmount
          amount={remainingBalance}
          cardEnding={last3Digits(remainingBalanceCardNumber)}
          infoMessage={infoMessage}
          type={type}
        />
      )}

      {unusedGiftCards && unusedGiftCards.length > 0 && (
        <div className={styles.unusedGiftCards} data-testid="payment-deductions-list-unused-gift-cards">
          <p>Gift cards not used as your balance has been covered:</p>

          <ul>
            {unusedGiftCards.map((item) => (
              <li key={item.id}>
                Gift card ending in {last3Digits(item.cardNumber)}. Remaining balance: {item.remainingBalance}
              </li>
            ))}
          </ul>
        </div>
      )}

      {shouldShowBalance && (
        <div className={styles.balance} data-testid="gift-cards-list-balance">
          <span>Balance to pay</span>
          <span>{balance}</span>
        </div>
      )}
    </div>
  );
};

export default PaymentDeductionsList;
